import { type ScaleLinear, axisTop, scaleLinear, select } from "d3";
import { Component, createRef } from "react";

import { type IBoxModel } from "../genome-viewer/interfaces.js";

export interface IColorScaleLegendProps {
  boxModel: IBoxModel;
  colors: string[];
  domain: number[];
  tickCount?: number;
  tickFormat?: (d: number) => string;
  title?: string;
}

/**
 * A legend for continuous gradient color scale (such as in the BLAST graphic hit summary view)
 *
 * created on 4/19/2017
 */
class ColorScaleLegend extends Component<IColorScaleLegendProps> {
  private scale: ScaleLinear<number, number>;
  private axis: any;
  private axisRef = createRef<SVGGElement>();

  constructor(props: IColorScaleLegendProps) {
    super(props);

    this.update(props);
  }

  componentDidMount() {
    this.renderAxis();
  }

  componentDidUpdate() {
    this.update(this.props);
    this.renderAxis();
  }

  update = (props: IColorScaleLegendProps) => {
    this.scale = scaleLinear()
      .domain(props.domain)
      .range([0, this.getLegendWidth(props)]);

    let axis = axisTop(this.scale);
    if (props.tickCount) {
      axis = axis.ticks(props.tickCount);
    }
    if (props.tickFormat) {
      axis = axis.tickFormat(props.tickFormat);
    }
    this.axis = axis;
  };

  renderAxis() {
    select(this.axisRef.current).call(this.axis);
  }

  getLegendWidth(props: IColorScaleLegendProps) {
    const bm: IBoxModel = props.boxModel;
    return (bm.width - bm.padding.left - bm.padding.right) * 0.5;
  }

  render() {
    const bm: IBoxModel = this.props.boxModel;
    const contentWidth: number = bm.width - bm.padding.left - bm.padding.right;
    const contentHeight: number = bm.height - bm.padding.top - bm.padding.bottom;

    const legendWidth = this.getLegendWidth(this.props);
    const halfHeight = contentHeight / 2;

    const translateContent = `translate(${bm.padding.left},${bm.padding.top})`;
    const translateWrapper = `translate(${contentWidth / 2 - 10},${halfHeight})`;
    const translateAxis = `translate(${-legendWidth / 2},${contentHeight - 5})`;
    const translateLabel = `translate(${-legendWidth / 2 - 10},${contentHeight - 2})`;

    return (
      <g className="color-scale-legend" transform={translateContent}>
        <defs>
          <linearGradient id="gradient-colors" x1="0%" y1="0%" x2="100%" y2="0%">
            {this.props.colors.map((color: string, index: number) => {
              return (
                <stop
                  offset={index / (this.props.colors.length - 1)}
                  stopColor={color}
                  key={`gradient-colors-stop-${index}`}
                />
              );
            })}
          </linearGradient>
        </defs>

        <g className="legend-wrapper" transform={translateWrapper}>
          <rect
            className="legend-box"
            x={-legendWidth / 2 - 50}
            y={-10}
            width={legendWidth + 70}
            height={contentHeight + 20}
          />

          <g className="axis" ref={this.axisRef} transform={translateAxis} />

          <rect
            className="legend-rect"
            x={-legendWidth / 2}
            y={halfHeight}
            width={legendWidth + 1}
            height={halfHeight}
            fill="url(#gradient-colors)"
          />

          {!!this.props.title && (
            <text textAnchor="end" className="label-text" transform={translateLabel}>
              {this.props.title}
            </text>
          )}
        </g>
      </g>
    );
  }
}

export default ColorScaleLegend;
